import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaHandshake, FaLink, FaEnvelope, FaGlobe } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Partners.css';

const Partners = () => {
  const mainColor = '#006186';
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/partners');
      
      if (response.data && response.data.length > 0) {
        setPartners(response.data);
      } else {
        setPartners([]);
      }
      
      setError(null);
    } catch (err) {
      console.error('Ошибка загрузки партнеров:', err);
      setError('Не удалось загрузить список партнеров');
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  const getDescription = (partner) => {
    return partner.description || 'Описание отсутствует';
  };

  const getName = (partner) => {
    return partner.name || 'Без названия';
  };

  const getLink = (partner) => {
    return partner.website_url || '#';
  };

  if (loading) {
    return (
      <div className="partners-page bg-white">
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Загрузка партнеров...</p>
        </Container>
      </div>
    );
  }

  return (
    <div className="partners-page bg-white">
      <section className="news-hero py-5 border-bottom">
        <Container>
          <h1 className="display-4 fw-bold mb-3" style={{ color: mainColor }}>
            Партнеры
          </h1>
          <div className="header-line mb-4" style={{ backgroundColor: mainColor, width: '80px', height: '5px' }}></div>
          <p className="lead text-muted mb-0" style={{ maxWidth: '850px' }}>
            Ассоциация выпускников ОшТУ реализует свои проекты благодаря поддержке меценатов
            и корпоративных партнёров. Мы создаем мост между образованием и реальным бизнесом.
          </p>
        </Container>
      </section>

      <Container className="py-5">
        <Row className="mb-5">
          <Col>
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
              <Row className="g-0">
                <Col md={1} className="d-none d-md-flex align-items-center justify-content-center" style={{ backgroundColor: mainColor }}>
                  <FaHandshake color="white" size={40} />
                </Col>
                <Col md={11} className="p-4 p-lg-5 bg-light-subtle">
                  <h3 className="fw-bold mb-3" style={{ color: '#1a365d' }}>Присоединяйтесь к сообществу</h3>
                  <p className="fs-5 text-secondary">
                    Мы приглашаем вашу компанию стать частью истории успеха ОшТУ. Поддержите таланты и получите доступ к кадровому резерву университета.
                  </p>
                  <div className="d-flex flex-wrap gap-3 mt-4">
                    <Button
                      as={Link}
                      to="/contact"
                      className="fw-bold px-4 py-2 border-0"
                      style={{ backgroundColor: mainColor }}
                    >
                      <FaEnvelope className="me-2" /> Написать нам
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <h2 className="fw-bold mb-4 d-flex align-items-center">
          Наши партнеры <div className="ms-3 flex-grow-1 border-bottom opacity-10"></div>
        </h2>

        {error && (
          <Alert variant="warning" className="mb-4">
            {error}. Показываем сохраненные данные.
          </Alert>
        )}

        {partners.length === 0 ? (
          <p className="text-center text-muted py-5">Список партнеров пуст</p>
        ) : (
          <Row className="g-4">
            {partners.map((partner, index) => (
              <Col key={partner.id || index} lg={4} md={6}>
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                  className="h-100"
                >
                  <Card className="h-100 border-0 shadow-sm rounded-4 p-4 partner-card">
                    <div className="d-flex align-items-center mb-3">
                      <div className="partner-icon-placeholder me-3">
                        {partner.logo_url ? (
                          <img 
                            src={partner.logo_url} 
                            alt={getName(partner)}
                            style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                          />
                        ) : (
                          <FaGlobe style={{ color: mainColor, opacity: 0.5 }} />
                        )}
                      </div>
                      <h5 className="fw-bold mb-0" style={{ color: '#1a365d', fontSize: '1.1rem' }}>
                        {getName(partner)}
                      </h5>
                    </div>
                    <Card.Text className="small text-muted flex-grow-1">
                      {getDescription(partner)}
                    </Card.Text>
                    <div className="mt-3 pt-3 border-top d-flex justify-content-between align-items-center">
                      <span className="small fw-bold text-uppercase" style={{ color: mainColor, fontSize: '0.7rem', letterSpacing: '1px' }}>
                        {partner.category || 'Партнер'}
                      </span>
                      {getLink(partner) !== '#' && (
                        <a 
                          href={getLink(partner)} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-muted"
                        >
                          <FaLink size={14} style={{ cursor: 'pointer' }} />
                        </a>
                      )}
                    </div>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </div>
  );
};

export default Partners;